import classNames from 'classnames';
import React, { useId } from 'react';
import { notNil } from '../../core/helpers/is-nil';
import { FormHelperText } from '../form-helper-text/form-helper-text';
import { FormInlineNotification, InlineNotification } from '../form-inline-notification/form-inline-notification';
import { FormLabel } from '../form-label/form-label';
import { Spacer } from '../spacer/spacer';
import ResizeHandle from './assets/resize-handle.svg?react';
import styles from './text-area.module.scss';

type Props = {
  className?: string;
  label: string;
  mandatory?: boolean;
  placeholder?: string;
  inlineNotification?: InlineNotification;
  helperMsg?: string;
  isDirty?: boolean;
} & React.InputHTMLAttributes<HTMLTextAreaElement>;

export const TextArea: React.FC<Props> = ({
  className,
  label,
  mandatory,
  placeholder,
  inlineNotification,
  disabled,
  value,
  helperMsg,
  isDirty = false,
  ...rest
}) => {
  const inputId = useId();
  const helperId = useId();

  const hasHelper = notNil(helperMsg);
  const hasInlineNotification = notNil(inlineNotification);
  const hasError = notNil(inlineNotification) && inlineNotification.type === 'error';

  return (
    <div className={styles.root}>
      <FormLabel disabled={disabled} htmlFor={inputId} mandatory={mandatory}>
        {label}
      </FormLabel>
      <div className={styles.innerContainer}>
        <div
          className={classNames(styles.textAreaContainer, { [styles.disabled]: disabled, [styles.isDirty]: isDirty })}
        >
          <textarea
            aria-describedby={hasHelper ? helperId : undefined}
            aria-invalid={hasError}
            className={classNames(styles.textArea, className)}
            disabled={disabled}
            id={inputId}
            placeholder={placeholder}
            required={mandatory}
            {...rest}
          />
          <div className={styles.resizeContainer}>
            <ResizeHandle className={styles.resizeHandle} />
          </div>
        </div>
        {hasInlineNotification ? (
          <FormInlineNotification type={inlineNotification.type}>{inlineNotification.message}</FormInlineNotification>
        ) : null}
        {hasHelper ? (
          <>
            <Spacer size="03" />
            <FormHelperText disabled={disabled} id={helperId}>
              {helperMsg}
            </FormHelperText>
          </>
        ) : null}
      </div>
    </div>
  );
};
